/**
 * PlanContext — single source of truth for subscription plan + feature gating
 * on the frontend.
 *
 * Mirrors what the backend enforces. Every gated module should check
 * `hasFeature("...")` BEFORE rendering interactive content; backend still
 * blocks API calls with 402 if the user tries to bypass the UI.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../constants/api';
import { useAuth } from './AuthContext';

const PlanContext = createContext(null);

const EMPTY_PLAN = {
  plan_id: 'free',
  plan_name: 'Free',
  is_pro: false,
  features: ['profile_edit', 'appearance'],
  limits: { max_profiles: 1 },
  usage: { profiles_used: 0, profiles_remaining: 1 },
  status: 'active',
  expires_at: null,
};

export const PlanProvider = ({ children }) => {
  const { user } = useAuth();
  const [plan, setPlan] = useState(EMPTY_PLAN);
  const [loading, setLoading] = useState(true);
  const [upgradeModal, setUpgradeModal] = useState({ open: false, feature: null });
  const [disabledSections, setDisabledSections] = useState([]);

  const fetchPlan = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setPlan(EMPTY_PLAN);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/plans/me`);
      setPlan(res.data);
    } catch (err) {
      console.warn('[PlanContext] Could not fetch plan, falling back to Free:', err?.response?.status);
      setPlan(EMPTY_PLAN);
    } finally {
      setLoading(false);
    }
  }, []);

  // "Coming soon" sections — admin-controlled, platform-wide, independent of
  // plan gating. The section stays fully visible; only its real actions get
  // intercepted (see ComingSoonOverlay).
  const fetchDisabledSections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/disabled-sections`);
      setDisabledSections(res.data.disabled_sections || []);
    } catch (err) {
      setDisabledSections([]);
    }
  }, []);

  // Refresh plan when the user changes (login/logout) or on mount
  useEffect(() => {
    if (user) {
      fetchPlan();
      fetchDisabledSections();
    } else {
      setPlan(EMPTY_PLAN);
      setDisabledSections([]);
      setLoading(false);
    }
  }, [user, fetchPlan, fetchDisabledSections]);

  // Plan changes that happen outside this tab (an admin editing it in
  // another window, a subscription payment approved minutes ago on
  // MercadoPago's site, an expiration cron running server-side) never
  // reach this tab on their own — there's no polling or websocket. Coming
  // back to the tab is the one moment worth checking again: cheap (one
  // request, only on actual tab focus) and it's exactly when someone who
  // just finished paying in another tab would look back at Contact.
  useEffect(() => {
    if (!user) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchPlan();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, fetchPlan]);

  const openUpgrade = useCallback((feature = null) => {
    setUpgradeModal({ open: true, feature });
  }, []);

  const closeUpgrade = useCallback(() => {
    setUpgradeModal({ open: false, feature: null });
  }, []);

  const hasFeature = useCallback(
    (feature) => Boolean(plan?.features?.includes(feature)),
    [plan]
  );

  const isSectionDisabled = useCallback(
    (section) => disabledSections.includes(section),
    [disabledSections]
  );

  const value = {
    plan,
    loading,
    isPro: !!plan?.is_pro,
    isFree: !plan?.is_pro,
    hasFeature,
    isSectionDisabled,
    profileLimit: plan?.limits?.max_profiles ?? 1,
    profilesUsed: plan?.usage?.profiles_used ?? 0,
    requiresProfileSelection: !!plan?.usage?.requires_profile_selection,
    activeProfiles: plan?.usage?.active_profiles ?? 0,
    refreshPlan: fetchPlan,
    upgradeModal,
    openUpgrade,
    closeUpgrade,
  };

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
};

export const usePlan = () => {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error('usePlan must be used inside <PlanProvider>');
  return ctx;
};

export default PlanContext;
